/* eslint-disable jsx-a11y/anchor-is-valid, jsx-a11y/img-redundant-alt */
import React from "react";
import { Link, useParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import { FiArrowUpRight } from "react-icons/fi";
import { pageTitle } from "../../helpers/PageTitle";
import { blogsData, getBlogBySlug } from "../../data/blogsData";
import ErrorPage from "./ErrorPage";

export default function BlogDetailsPage() {
  const { blogDetailsId } = useParams();
  const blog = getBlogBySlug(blogDetailsId);

  if (!blog) return <ErrorPage />;

  pageTitle(`${blog.title} | Blog`);

  return (
    <main className="relative overflow-hidden bg-[#fbfdfd]">
      {/* Background decorations */}
      <div className="pointer-events-none absolute left-[-250px] top-[100px] h-[600px] w-[600px] rounded-full bg-teal-100/30 blur-3xl" />
      <div className="pointer-events-none absolute right-[-250px] top-[500px] h-[600px] w-[600px] rounded-full bg-emerald-100/30 blur-3xl" />
      <div className="pointer-events-none absolute left-[30%] top-[1500px] h-[500px] w-[500px] rounded-full bg-green-50/40 blur-3xl" />
      <div className="pointer-events-none absolute right-[20%] top-[2500px] h-[400px] w-[400px] rounded-full bg-teal-50/40 blur-3xl" />

      {/* ============ HERO SECTION ============ */}
      <section className="relative z-10 overflow-hidden px-4 pt-[160px] pb-[100px] sm:px-6 max-[991px]:pt-[130px] max-[991px]:pb-[70px]">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/80 via-white to-teal-50/50" />
        
        {/* Decorative grid pattern */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: "linear-gradient(#1b667a 1px, transparent 1px), linear-gradient(90deg, #1b667a 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />

        {/* Floating decorative elements */}
        <div className="pointer-events-none absolute left-[8%] top-[25%]">
          <div className="h-24 w-24 rounded-3xl border-2 border-emerald-200/40 bg-gradient-to-br from-emerald-100/50 to-transparent rotate-12 opacity-60 animate-pulse" />
        </div>
        <div className="pointer-events-none absolute right-[10%] top-[20%]">
          <div className="h-20 w-20 rounded-full border-2 border-teal-200/40 bg-gradient-to-br from-teal-100/50 to-transparent opacity-50" />
        </div>
        <div className="pointer-events-none absolute left-[15%] bottom-[15%]">
          <div className="h-16 w-16 rounded-2xl border border-emerald-200/30 bg-emerald-50/30 rotate-45 opacity-40" />
        </div>
        <div className="pointer-events-none absolute right-[15%] bottom-[20%]">
          <Icon icon="mdi:star" className="h-8 w-8 text-emerald-200/40" />
        </div>

        {/* Decorative dots */}
        <div
          className="pointer-events-none absolute left-[4%] top-[30%] h-48 w-64 opacity-30"
          style={{
            backgroundImage: "radial-gradient(#1b667a 1.5px, transparent 1.5px)",
            backgroundSize: "16px 16px",
          }}
        />
        <div
          className="pointer-events-none absolute right-[6%] top-[25%] h-40 w-56 opacity-25"
          style={{
            backgroundImage: "radial-gradient(#10B981 1px, transparent 1px)",
            backgroundSize: "12px 12px",
          }}
        />

        <div className="relative mx-auto max-w-[900px] text-center">
          {/* Breadcrumb */}
          <div className="mb-8 flex items-center justify-center gap-2 text-[14px] text-gray-400">
            <Link to="/" className="flex items-center gap-1.5 hover:text-[#1b667a] transition-colors">
              <Icon icon="mdi:home" className="w-4 h-4" />
              Home
            </Link>
            <Icon icon="mdi:chevron-right" className="w-4 h-4" />
            <Link to="/blog" className="hover:text-[#1b667a] transition-colors">Blog</Link>
            <Icon icon="mdi:chevron-right" className="w-4 h-4" />
            <span className="text-gray-600 font-medium">{blog.category}</span>
          </div>

          {/* Category Badge */}
          <div className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-emerald-200 bg-white/80 px-6 py-2.5 shadow-[0_4px_20px_rgba(16,185,129,0.1)] backdrop-blur-sm">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#1b667a]"></span>
            </span>
            <span className="text-[12px] font-bold tracking-[2.5px] text-emerald-600 uppercase">
              {blog.category}
            </span>
          </div>

          {/* Heading */}
          <h1 className="mb-8 text-[clamp(38px,5.5vw,60px)] font-bold leading-[1.08] tracking-[-0.03em] text-gray-900">
            {blog.title}
          </h1>

          {/* Subtitle */}
          <p className="mx-auto mb-10 max-w-[650px] text-[18px] leading-[1.8] text-gray-500">
            Discover how artificial intelligence is reshaping industries and creating unprecedented opportunities for growth and innovation.
          </p>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center justify-center gap-4 text-[14px] text-gray-500">
            <div className="flex items-center gap-2.5 rounded-full border border-gray-100 bg-white px-4 py-2 shadow-sm">
              <img
                src={blog.author.avatar}
                alt={blog.author.name}
                className="h-8 w-8 rounded-full object-cover ring-2 ring-emerald-100"
              />
              <span className="font-semibold text-gray-900">{blog.author.name}</span>
            </div>
            <div className="flex items-center gap-2 rounded-full border border-gray-100 bg-white px-4 py-2 shadow-sm">
              <Icon icon="mdi:calendar-outline" className="w-4 h-4 text-[#1b667a]" />
              <span>{blog.date}</span>
            </div>
            <div className="flex items-center gap-2 rounded-full border border-gray-100 bg-white px-4 py-2 shadow-sm">
              <Icon icon="mdi:clock-outline" className="w-4 h-4 text-[#1b667a]" />
              <span>{blog.readTime}</span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="mt-10 flex items-center justify-center gap-4">
            <button className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-5 py-2.5 text-[14px] font-medium text-gray-600 shadow-sm transition-all duration-300 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-600">
              <Icon icon="mdi:heart-outline" className="w-5 h-5" />
              {blog.likes}
            </button>
            <button className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-5 py-2.5 text-[14px] font-medium text-gray-600 shadow-sm transition-all duration-300 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-600">
              <Icon icon="mdi:bookmark-outline" className="w-5 h-5" />
              Save
            </button>
            <button className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-5 py-2.5 text-[14px] font-medium text-gray-600 shadow-sm transition-all duration-300 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-600">
              <Icon icon="mdi:share-variant-outline" className="w-5 h-5" />
              Share
            </button>
          </div>
        </div>
      </section>

      {/* ============ HERO IMAGE ============ */}
      <section className="relative z-10 px-4 sm:px-6 -mt-12">
        <div className="mx-auto max-w-[1100px]">
          <div className="relative overflow-hidden rounded-[36px] shadow-[0_50px_120px_rgba(15,23,42,0.2)]">
            <img
              src={blog.heroImage}
              alt={blog.title}
              className="h-[400px] w-full object-cover sm:h-[500px] lg:h-[550px]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
            
            {/* Overlay badge */}
            <div className="absolute left-6 top-6 rounded-full border border-white/20 bg-black/30 px-5 py-2.5 backdrop-blur-md">
              <div className="flex items-center gap-2.5">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400"></span>
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[2.5px] text-white">
                  Featured Article
                </span>
              </div>
            </div>

            {/* Bottom overlay info */}
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-white/90">
                  <Icon icon="mdi:heart" className="w-5 h-5" />
                  <span className="text-[14px] font-medium">{blog.likes} likes</span>
                </div>
                <div className="h-4 w-px bg-white/30" />
                <div className="flex items-center gap-2 text-white/90">
                  <Icon icon="mdi:comment-outline" className="w-5 h-5" />
                  <span className="text-[14px] font-medium">24 comments</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ KEY TAKEAWAYS ============ */}
      <section className="relative z-10 px-4 sm:px-6 py-20 lg:py-24">
        <div className="mx-auto max-w-[1100px]">
          {/* Section header */}
          <div className="mb-12 text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5">
              <Icon icon="mdi:lightbulb-outline" className="w-4 h-4 text-[#1b667a]" />
              <span className="text-[11px] font-bold tracking-[2px] text-emerald-600 uppercase">Key Takeaways</span>
            </div>
            <h2 className="text-[clamp(24px,3vw,32px)] font-bold text-gray-900">
              What You'll Learn
            </h2>
          </div>

          <div className="grid gap-6 sm:grid-cols-3">
            {blog.keyTakeaways.map((item, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-[24px] border border-gray-100 bg-white p-8 shadow-[0_4px_25px_rgba(0,0,0,0.05)] transition-all duration-500 hover:-translate-y-3 hover:shadow-[0_25px_60px_rgba(0,0,0,0.12)]"
              >
                {/* Decorative corner */}
                <div
                  className="absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-10 transition-opacity duration-500 group-hover:opacity-20"
                  style={{ background: `radial-gradient(circle, ${item.color}, transparent 70%)` }}
                />
                
                <div
                  className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl transition-all duration-300 group-hover:scale-110"
                  style={{
                    background: `linear-gradient(135deg, ${item.color}15, ${item.color}25)`,
                    border: `2px solid ${item.color}30`,
                  }}
                >
                  <Icon icon={item.icon} className="w-8 h-8" style={{ color: item.color }} />
                </div>
                <h3 className="mb-3 text-[20px] font-bold text-gray-900">{item.title}</h3>
                <p className="text-[16px] leading-[1.75] text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ BLOG CONTENT ============ */}
      <section className="relative z-10 px-4 sm:px-6 py-16 lg:py-20">
        <div className="mx-auto max-w-[850px]">
          {/* Author Card */}
          <div className="mb-16 overflow-hidden rounded-[28px] border border-gray-100 bg-white shadow-[0_10px_40px_rgba(0,0,0,0.07)] transition-all duration-500 hover:shadow-[0_20px_60px_rgba(0,0,0,0.12)]">
            {/* Header with gradient */}
            <div className="relative h-32 overflow-hidden bg-gradient-to-r from-[#1b667a] via-[#10a879] to-[#1b667a]">
              <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "12px 12px" }} />
              <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10" />
              <div className="absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-white/10" />
            </div>
            
            <div className="relative px-8 pb-8 pt-0">
              {/* Avatar + Name Row */}
              <div className="flex flex-col sm:flex-row items-start sm:items-end gap-5 -mt-16">
                <div className="relative">
                  <img
                    src={blog.author.avatar}
                    alt={blog.author.name}
                    className="h-28 w-28 rounded-2xl object-cover ring-4 ring-white shadow-xl"
                  />
                  <div className="absolute -bottom-2 -right-2 flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-r from-[#1b667a] to-[#10a879] shadow-lg">
                    <Icon icon="mdi:check-bold" className="w-4 h-4 text-white" />
                  </div>
                </div>
                
                <div className="flex-1">
                  <h4 className="mb-1 text-[24px] font-bold text-gray-900">{blog.author.name}</h4>
                  <p className="flex items-center gap-2 text-[15px] font-semibold text-[#1b667a]">
                    <Icon icon="mdi:briefcase-outline" className="w-4 h-4" />
                    {blog.author.role}
                  </p>
                </div>
                
                <div className="flex items-center gap-3">
                  <a href="#" className="group flex h-11 w-11 items-center justify-center rounded-xl border border-gray-100 bg-gray-50 text-gray-400 transition-all duration-300 hover:border-[#0A66C2] hover:bg-[#0A66C2] hover:text-white hover:shadow-lg hover:shadow-[#0A66C2]/25">
                    <Icon icon="fa6-brands:linkedin-in" className="w-5 h-5" />
                  </a>
                  <a href="#" className="group flex h-11 w-11 items-center justify-center rounded-xl border border-gray-100 bg-gray-50 text-gray-400 transition-all duration-300 hover:border-[#1DA1F2] hover:bg-[#1DA1F2] hover:text-white hover:shadow-lg hover:shadow-[#1DA1F2]/25">
                    <Icon icon="fa6-brands:twitter" className="w-5 h-5" />
                  </a>
                  <a href="#" className="group flex h-11 w-11 items-center justify-center rounded-xl border border-gray-100 bg-gray-50 text-gray-400 transition-all duration-300 hover:border-[#E4405F] hover:bg-[#E4405F] hover:text-white hover:shadow-lg hover:shadow-[#E4405F]/25">
                    <Icon icon="fa6-brands:instagram" className="w-5 h-5" />
                  </a>
                </div>
              </div>
              
              {/* Bio */}
              <p className="mt-6 text-[16px] leading-[1.75] text-gray-500">{blog.author.bio}</p>
              
              {/* Stats */}
              <div className="mt-6 flex flex-wrap items-center gap-6 rounded-2xl bg-gray-50 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-sm">
                    <Icon icon="mdi:post-outline" className="w-5 h-5 text-[#1b667a]" />
                  </div>
                  <div>
                    <span className="block text-[18px] font-bold text-gray-900">{blog.author.articles}</span>
                    <span className="text-[12px] text-gray-400">Articles</span>
                  </div>
                </div>
                <div className="h-8 w-px bg-gray-200" />
              </div>
            </div>
          </div>

          {/* Blog Content */}
          <div className="space-y-10">
            {blog.content.map((block, index) => {
              switch (block.type) {
                case "paragraph":
                  return (
                    <p key={index} className="text-[19px] leading-[1.95] text-gray-600">
                      {block.text}
                    </p>
                  );
                case "heading":
                  return (
                    <div key={index} className="pt-8">
                      <div className="mb-4 flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <div className="h-[3px] w-12 bg-gradient-to-r from-[#1b667a] to-[#10a879] rounded-full" />
                          <div className="h-2.5 w-2.5 rounded-full bg-[#1b667a]" />
                        </div>
                      </div>
                      <h2 className="text-[clamp(28px,4vw,38px)] font-bold text-gray-900 leading-[1.2]">
                        {block.text}
                      </h2>
                    </div>
                  );
                case "callout":
                  return (
                    <div key={index} className="relative overflow-hidden rounded-[20px] border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-6 sm:p-8">
                      <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-amber-100/50" />
                      <div className="relative flex items-start gap-4">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-600">
                          <Icon icon={block.icon} className="w-6 h-6" />
                        </div>
                        <div>
                          <h4 className="mb-2 text-[16px] font-bold text-amber-900">{block.title}</h4>
                          <p className="text-[17px] leading-[1.75] text-amber-800">{block.text}</p>
                        </div>
                      </div>
                    </div>
                  );
                case "list":
                  return (
                    <div key={index} className="overflow-hidden rounded-[24px] border border-emerald-100 bg-gradient-to-br from-emerald-50/60 via-white to-teal-50/40 p-8 sm:p-10">
                      <ul className="space-y-5">
                        {block.items.map((item, i) => (
                          <li key={i} className="flex items-start gap-4 text-[18px] leading-[1.8] text-gray-700">
                            <div className="mt-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#1b667a] to-[#10a879] text-white shadow-[0_4px_12px_rgba(27,102,122,0.3)]">
                              <Icon icon="mdi:check" className="w-4 h-4" />
                            </div>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                case "stats":
                  return (
                    <div key={index} className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                      {block.items.map((stat, i) => (
                        <div key={i} className="group relative overflow-hidden rounded-[20px] border border-gray-100 bg-white p-6 text-center shadow-[0_4px_20px_rgba(0,0,0,0.04)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_15px_40px_rgba(0,0,0,0.1)]">
                          <div className="mb-3 flex h-12 w-12 mx-auto items-center justify-center rounded-xl bg-emerald-50 text-[#1b667a] transition-all duration-300 group-hover:bg-[#1b667a] group-hover:text-white group-hover:scale-110">
                            <Icon icon={stat.icon} className="w-6 h-6" />
                          </div>
                          <p className="text-[28px] font-bold text-gray-900">{stat.value}</p>
                          <p className="text-[13px] font-medium text-gray-500">{stat.label}</p>
                        </div>
                      ))}
                    </div>
                  );
                case "imageGrid":
                  return (
                    <div key={index} className="my-14 grid grid-cols-3 gap-5">
                      {block.images.map((img, i) => (
                        <div key={i} className="group relative overflow-hidden rounded-[24px] shadow-[0_10px_40px_rgba(0,0,0,0.1)] transition-all duration-500 hover:-translate-y-3 hover:shadow-[0_25px_60px_rgba(0,0,0,0.18)]">
                          <img
                            src={img}
                            alt={`Blog image ${i + 1}`}
                            className="h-[180px] w-full object-cover transition-transform duration-700 group-hover:scale-110 sm:h-[240px]"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300" />
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 text-[#1b667a] shadow-lg transform scale-75 group-hover:scale-100 transition-transform duration-300">
                              <Icon icon="mdi:fullscreen" className="w-6 h-6" />
                            </div>
                          </div>
                          <div className="absolute bottom-4 left-4 right-4">
                            <div className="flex items-center gap-2 text-white opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0">
                              <Icon icon="mdi:image-multiple" className="w-4 h-4" />
                              <span className="text-[12px] font-medium">View Image {i + 1}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                case "steps":
                  return (
                    <div key={index} className="overflow-hidden rounded-[24px] border border-gray-100 bg-white p-8 sm:p-10 shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
                      <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
                        {block.items.map((step, i) => (
                          <div key={i} className="text-center">
                            <div className="mb-4 flex h-14 w-14 mx-auto items-center justify-center rounded-2xl bg-gradient-to-br from-[#1b667a] to-[#10a879] text-[20px] font-bold text-white shadow-[0_6px_20px_rgba(27,102,122,0.3)]">
                              {step.num}
                            </div>
                            <h4 className="mb-2 text-[17px] font-bold text-gray-900">{step.title}</h4>
                            <p className="text-[14px] leading-[1.6] text-gray-500">{step.desc}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                case "quote":
                  return (
                    <blockquote key={index} className="my-14 overflow-hidden rounded-[24px] border border-emerald-100 bg-gradient-to-br from-emerald-50/60 via-white to-teal-50/40 p-10 sm:p-12">
                      <div className="mb-6 flex items-center gap-3">
                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1b667a] to-[#10a879] text-white shadow-[0_6px_20px_rgba(27,102,122,0.3)]">
                          <Icon icon="mdi:format-quote-open" className="w-7 h-7" />
                        </div>
                      </div>
                      <p className="mb-8 text-[22px] italic leading-[1.8] text-gray-700">
                        {block.text}
                      </p>
                      <div className="flex items-center gap-4">
                        <div className="h-[3px] w-12 bg-gradient-to-r from-[#1b667a] to-[#10a879] rounded-full" />
                        <cite className="text-[16px] font-bold text-[#1b667a] not-italic">
                          {block.author}
                        </cite>
                      </div>
                    </blockquote>
                  );
                default:
                  return null;
              }
            })}
          </div>

          {/* Tags */}
          <div className="mt-16 flex flex-wrap items-center gap-3">
            <span className="text-[16px] font-bold text-gray-900">Tags:</span>
            {blog.tags.map((tag, index) => {
              const tagColors = [
                { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", hover: "hover:bg-[#1b667a] hover:text-white hover:border-[#1b667a]" },
                { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700", hover: "hover:bg-sky-500 hover:text-white hover:border-sky-500" },
                { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", hover: "hover:bg-violet-500 hover:text-white hover:border-violet-500" },
                { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", hover: "hover:bg-amber-500 hover:text-white hover:border-amber-500" },
                { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", hover: "hover:bg-rose-500 hover:text-white hover:border-rose-500" },
              ];
              const color = tagColors[index % tagColors.length];
              return (
                <span
                  key={index}
                  className={`rounded-full border ${color.border} ${color.bg} ${color.text} px-5 py-2.5 text-[14px] font-semibold shadow-sm transition-all duration-300 ${color.hover} hover:shadow-md cursor-pointer`}
                >
                  {tag}
                </span>
              );
            })}
          </div>

          {/* Share Section */}
          <div className="mt-14 overflow-hidden rounded-[28px] border border-gray-100 bg-white shadow-[0_10px_40px_rgba(0,0,0,0.07)]">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-8 p-8 sm:p-10">
              <div className="text-center sm:text-left">
                <p className="mb-2 text-[20px] font-bold text-gray-900">Enjoyed this article?</p>
                <p className="text-[16px] text-gray-500">Share it with your network and help others discover these insights.</p>
              </div>
              <div className="flex items-center gap-3">
                <a href="#" className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#1DA1F2]/10 text-[#1DA1F2] transition-all duration-300 hover:bg-[#1DA1F2] hover:text-white hover:scale-110 hover:shadow-lg">
                  <Icon icon="fa6-brands:twitter" className="w-6 h-6" />
                </a>
                <a href="#" className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#4267B2]/10 text-[#4267B2] transition-all duration-300 hover:bg-[#4267B2] hover:text-white hover:scale-110 hover:shadow-lg">
                  <Icon icon="fa6-brands:facebook-f" className="w-6 h-6" />
                </a>
                <a href="#" className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0A66C2]/10 text-[#0A66C2] transition-all duration-300 hover:bg-[#0A66C2] hover:text-white hover:scale-110 hover:shadow-lg">
                  <Icon icon="fa6-brands:linkedin-in" className="w-6 h-6" />
                </a>
                <a href="#" className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 text-gray-600 transition-all duration-300 hover:bg-gray-800 hover:text-white hover:scale-110 hover:shadow-lg">
                  <Icon icon="mdi:link" className="w-6 h-6" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ RELATED POSTS ============ */}
      <section className="relative z-10 px-4 sm:px-6 py-20 lg:py-28">
        <div className="mx-auto max-w-[1200px]">
          <div className="mb-16 text-center">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-5 py-2">
              <Icon icon="mdi:book-open-variant" className="w-5 h-5 text-[#1b667a]" />
              <span className="text-[12px] font-bold tracking-[2.5px] text-emerald-600 uppercase">
                Keep Reading
              </span>
            </div>
            <h2 className="text-[clamp(32px,4.5vw,52px)] font-bold text-gray-900">
              Related{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1b667a] via-[#10a879] to-[#10B981]">
                Articles
              </span>
            </h2>
          </div>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {blogsData.filter(b => b.slug !== blog.slug).slice(0, 3).map((post, index) => (
              <Link
                key={index}
                to={`/blog/${post.slug}`}
                className="group relative overflow-hidden rounded-[28px] border border-gray-100 bg-white shadow-[0_10px_40px_rgba(0,0,0,0.07)] transition-all duration-500 hover:-translate-y-4 hover:shadow-[0_30px_70px_rgba(0,0,0,0.15)]"
              >
                <div className="relative h-[240px] overflow-hidden">
                  <img
                    src={post.heroImage}
                    alt={post.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <div className="absolute left-5 top-5 rounded-full bg-white/90 px-4 py-2 backdrop-blur-sm shadow-sm">
                    <span className="text-[12px] font-bold text-[#1b667a]">{post.category}</span>
                  </div>
                  <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                    <p className="text-[13px] font-medium text-white/90">{post.date}</p>
                    <p className="text-[12px] font-medium text-emerald-300">{post.readTime}</p>
                  </div>
                </div>
                <div className="p-7">
                  <h3 className="mb-3 text-[22px] font-bold text-gray-900 leading-[1.3] group-hover:text-[#1b667a] transition-colors">
                    {post.title}
                  </h3>
                  <p className="mb-5 text-[16px] leading-[1.75] text-gray-500 line-clamp-2">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center gap-2 text-[15px] font-semibold text-[#1b667a]">
                    Read Article
                    <FiArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

    </main>
  );
}
